import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
  CLink,
  CInputFile,
} from "@coreui/react";
import { useEffect, useState, useRef } from "react";
import { Spinner } from "react-bootstrap";
import {
  createItem,
  updateItem,
  ItemRequestStatus,
  getItemPresignedUrl,
  getItem,
} from "../../api/generics";
import Errors, { getFieldErrors } from "../../models/errors";
import { SUCCESS } from "../../utils/constants/tags";
import { FieldErrors } from "../form/FieldErrors";
import { errorAlert } from "../utils/messages";
import { emptyValueOnUndefined } from "../../utils/fields";
import GeneratedRecord, {
  newGeneratedRecord,
} from "../../models/generated-record";
import { getObjectMinioUrl } from "../../minio/urls";

interface RecordPdfFormProps {
  initialRecord?: GeneratedRecord;
  initialErrors?: Errors;
  onCancel: () => void | Promise<void>;
  onSuccess: () => void | Promise<void>;
}

const RecordPdfForm: React.FC<RecordPdfFormProps> = ({
  initialRecord,
  initialErrors,
  onCancel,
  onSuccess,
}) => {
  const [editingRecord, setEditingRecord] = useState<GeneratedRecord>(
    initialRecord ? initialRecord : newGeneratedRecord()
  );

  const [loading, setLoading] = useState(true);

  const [errors, setErrors] = useState<Errors>(
    initialErrors ? initialErrors : {}
  );
  const [submitting, setSubmitting] = useState(false);

  const [loadedPdfPath, setLoadedPdfPath] = useState<string | undefined>(
    initialRecord?.pdfFile
  );
  const pdf = useRef<HTMLInputElement>(null);
  const pdfFileName = useRef<string>("");
  const pdfUploaded = useRef<boolean>(
    initialRecord ? initialRecord.pdfFile !== undefined : false
  );
  const pdfModified = useRef<boolean>(false);

  const onPDFChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    pdfModified.current = true;
    let fileParts = e.target.value.split("\\");
    pdfFileName.current = fileParts[fileParts.length - 1];
    setEditingRecord({
      ...editingRecord,
      pdfFile: pdfFileName.current,
    });
  };

  const handlePDFUpload = async () => {
    if (!pdfModified.current) {
      return true;
    }

    setSubmitting(true);
    let toSendRecord: GeneratedRecord = {
      ...editingRecord,
    };

    let presignedUrlPromise: Promise<ItemRequestStatus<{ url: string }>>;

    presignedUrlPromise = getItemPresignedUrl<GeneratedRecord>(
      `/records/put_pdf_url/`,
      toSendRecord
    );

    const pdfUrlStatus = await presignedUrlPromise;

    if (pdfUrlStatus.status !== SUCCESS) {
      if (pdfUrlStatus.errors !== undefined) {
        setErrors(pdfUrlStatus.errors);
      }

      let message = "Ha ocurrido un error!!";
      if (pdfUrlStatus.detail !== undefined) {
        message = pdfUrlStatus.detail;
      }
      errorAlert(message);
      return false;
    }

    if (pdfUrlStatus.data === undefined) {
      return false;
    }

    const urlData = pdfUrlStatus.data as { url: string };

    if (!pdf.current || !pdf.current.files) {
      return false;
    }
    const pdfFile = pdf.current.files[0];
    if (!pdfFile) {
      return false;
    }

    const uploadStatus = await fetch(urlData.url!, {
      method: "PUT",
      body: pdfFile,
      headers: {
        "Content-type": pdfFile.type,
      },
    });

    return uploadStatus.status === 200;
  };

  const onPDFDownloadClick = async (record: GeneratedRecord) => {
    const qrState = await getItem<{ url: string }>(
      `/records/${record.id}/pdf_url/`
    );

    if (qrState.status !== SUCCESS || qrState.data === undefined) {
      errorAlert("Error al tratar de obtener el Archivo");
      return;
    }
    window.open(getObjectMinioUrl(qrState.data.url), "_blank");
  };

  const onSave = async () => {
    setSubmitting(true);
    const hasPDF = editingRecord.pdfFile !== undefined;

    const pdfUploaded = hasPDF ? await handlePDFUpload() : true;
    if (!pdfUploaded) {
      setSubmitting(false);
      return;
    }

    let toSendRecord: GeneratedRecord = {
      ...editingRecord,
    };

    let requestPromise: Promise<ItemRequestStatus<GeneratedRecord>>;
    if (editingRecord.id === undefined) {
      requestPromise = createItem<GeneratedRecord>(
        "/records/create/",
        toSendRecord
      );
    } else {
      requestPromise = updateItem<GeneratedRecord>(
        `/records/${toSendRecord.id}/`,
        toSendRecord
      );
    }

    const recordStatus = await requestPromise;

    if (recordStatus.status !== SUCCESS) {
      if (recordStatus.errors !== undefined) {
        setErrors(recordStatus.errors);
      }

      let message = "Ha ocurrido un error!!";
      if (recordStatus.detail !== undefined) {
        message = recordStatus.detail;
      }
      errorAlert(message);
    } else {
      setErrors({});
      clearForm();
      setLoading(true);
      onSuccess();
    }
    setSubmitting(false);
  };

  const onClose = () => {
    clearForm();
    onCancel();
  };

  const clearForm = () => {
    setEditingRecord(newGeneratedRecord());
  };

  useEffect(() => {
    setLoading(true);
    setEditingRecord(initialRecord ? initialRecord : newGeneratedRecord());
    setLoadedPdfPath(initialRecord?.pdfFile);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRecord]);

  useEffect(() => {
    setErrors(initialErrors ? initialErrors : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialErrors]);
  const buttonText1 = loading ? "Cargando..." : "Guardar";
  const buttonText = submitting ? "Guardando..." : buttonText1;

  return (
    <>
      <fieldset disabled={submitting}>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Documento:</CLabel>
            </CCol>
            <CCol>
              <CInputFile
                accept=".pdf"
                type="file"
                innerRef={pdf}
                onChange={onPDFChange}
              ></CInputFile>
              {loadedPdfPath ? (
                <small>
                  Seleccionado actualmente:
                  <CLink
                    href={`#`}
                    target="_blank"
                    onClick={() => onPDFDownloadClick(editingRecord)}
                  >
                    {loadedPdfPath}
                  </CLink>
                </small>
              ) : (
                <></>
              )}

              <FieldErrors
                errors={getFieldErrors("pdfFile", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup className="float-right">
          <CButtonGroup>
            <CButton type="button" color="secondary" onClick={onClose}>
              Atras
            </CButton>
            <CButton type="submit" color="primary" onClick={onSave}>
              {submitting || loading ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : (
                <></>
              )}
              {buttonText}
            </CButton>
          </CButtonGroup>
        </CFormGroup>
      </fieldset>
    </>
  );
};

export default RecordPdfForm;
